/**
 * ConsentPop - Main widget class
 *
 * Orchestrates: ConfigManager → ConsentManager → EventReporter + GtagIntegration
 *
 * The widget must NEVER break a customer's site.
 * All errors are caught and logged, never thrown to the page.
 */

import { ConfigManager } from './ConfigManager';
import { ConsentManager } from './ConsentManager';
import { EventReporter } from './EventReporter';
import { GtagIntegration } from './GtagIntegration';
import type { ConsentPopInitConfig } from './types';

declare const __CONSENTPOP_DEBUG__: boolean;

const WIDGET_VERSION = '2.1.0';

export class ConsentPop {
  private initConfig: ConsentPopInitConfig;
  private initialized = false;
  private consentManager: ConsentManager | null = null;

  constructor(config: ConsentPopInitConfig) {
    this.initConfig = config;
  }

  /**
   * Initialize the consent banner.
   * Fetches config from API, then runs cookieconsent.
   */
  async init(): Promise<void> {
    if (this.initialized) {
      if (typeof __CONSENTPOP_DEBUG__ !== 'undefined' && __CONSENTPOP_DEBUG__) {
        console.debug('[ConsentPop] Already initialized, skipping');
      }
      return;
    }

    try {
      // Runtime events are deliberately separate from consent events so a
      // dashboard can distinguish a rendered widget from a saved config.
      const eventReporter = new EventReporter(this.initConfig);

      // 1. Fetch config (API → last-known config; fail closed if unavailable)
      const configManager = new ConfigManager(this.initConfig);
      const { config, geo, source } = await configManager.load();

      if (typeof __CONSENTPOP_DEBUG__ !== 'undefined' && __CONSENTPOP_DEBUG__) {
        console.debug('[ConsentPop] Config loaded:', { mode: config.mode, geo: geo.country, resolvedMode: geo.resolvedMode });
      }

      // 2. Set up Google Consent Mode v2 integration
      const gtagIntegration = new GtagIntegration();

      // 3. Initialize consent banner via ConsentManager
      const consentManager = new ConsentManager(config, geo, eventReporter, gtagIntegration);
      await consentManager.init();
      this.consentManager = consentManager;

      eventReporter.reportRuntime({
        event: 'init_success',
        hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
        configVersion: config.configVersion,
        themePreset: config.themePreset || 'premium',
        widgetVersion: WIDGET_VERSION,
        source,
      });

      this.initialized = true;

      if (typeof __CONSENTPOP_DEBUG__ !== 'undefined' && __CONSENTPOP_DEBUG__) {
        console.debug('[ConsentPop] Initialized successfully');
      }
    } catch (error) {
      const code = error instanceof Error
        ? (error as Error & { code?: string }).code
        : undefined;
      if (code === 'CONFIG_UNAVAILABLE') {
        new EventReporter(this.initConfig).reportRuntime({
          event: 'config_error',
          hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
          errorCode: code,
        });
      }

      // Subscription/rate limit errors — don't render, don't crash
      if (error instanceof Error) {
        if (code === 'SUBSCRIPTION_REQUIRED' || code === 'RATE_LIMITED') {
          if (typeof __CONSENTPOP_DEBUG__ !== 'undefined' && __CONSENTPOP_DEBUG__) {
            console.warn(`[ConsentPop] ${error.message} — banner will not render`);
          }
          return;
        }
      }

      // All other errors — log but never throw
      console.error('[ConsentPop] Initialization failed:', error);
    }
  }

  /**
   * Open the visitor's saved cookie preferences.
   *
   * Consumers must use this public method rather than querying the widget's
   * generated DOM. It is intentionally a no-op until initialization succeeds
   * so an unavailable configuration can never imply consent.
   */
  showPreferences(): boolean {
    if (!this.initialized || !this.consentManager) {
      return false;
    }

    this.consentManager.showPreferences();
    return true;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
